import React, { FC, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { getRemainingWaitingTime } from '@pages/ProductRegistration/AppropriatenessTest/appropriatenessTestStatus'
import { useProductAppropriatenessTestStatus } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useProductAppropriatenessTestStatus'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductWriteContext } from '@context/ProductContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CircleCloseIcon } from '@ui/icons/CircleClose.icon'

import {
  getAlternativeRegisteredBaseProduct,
  isAppropriatenessTestMaximumRetakeLimitReached,
  isBaseProduct,
} from '@utils/AccountAccess/accountProductStatuses'
import { CommonPaths } from '@utils/path'

import { TickmillProductType } from '@model/TickmillProductType'

import { useLandingPage } from '@hooks/useLandingPage'
import { useLogout } from '@hooks/useLogout'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { TestTemplate } from '@shared/templates/TestTemplate'

import { useResolvedAppTestProduct } from './hooks/useResolvedAppTestProduct'

export const ProductAppropriatenessTestFailedPage: FC = () => {
  useScrollToTop()
  const contentRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const resolvedProductId = useResolvedAppTestProduct()
  const { account } = useAccountReadContext()
  const { changeProductContext } = useProductWriteContext()
  const { getLandingPage } = useLandingPage()
  const { logout } = useLogout()

  const data = useProductAppropriatenessTestStatus(resolvedProductId)
  const { apTestAnswers, attempt, isLoading } = data

  const isAppTestMaximumRetakeLimitReached = isAppropriatenessTestMaximumRetakeLimitReached(
    account,
    resolvedProductId
  )
  const remainingWaitingTime = getRemainingWaitingTime(account, apTestAnswers)
  const { days, hours, minutes, seconds } = remainingWaitingTime
  const isTimeLeft = !!(days + hours + minutes + seconds)
  const fallbackProduct = getAlternativeRegisteredBaseProduct(account, resolvedProductId)
  const isSQFProduct = resolvedProductId === TickmillProductType.SQF

  useEffect(() => {
    if (apTestAnswers?.isClientVulnerable) {
      navigate('../denied')
      return
    }
  }, [apTestAnswers?.isClientVulnerable, logout, navigate])

  const handleClose = () => {
    if (isSQFProduct) {
      navigate(CommonPaths.TradersRoomWallets)
      return
    }

    if (fallbackProduct) {
      changeProductContext(fallbackProduct)
      navigate(getLandingPage(fallbackProduct))
      return
    }

    navigate(getLandingPage())
  }

  const isTestFailedVisible =
    !isLoading &&
    (isAppTestMaximumRetakeLimitReached || attempt === 3 || !isBaseProduct(resolvedProductId))

  if (isTestFailedVisible) {
    const getTitle = () => {
      switch (resolvedProductId) {
        case TickmillProductType.SQF:
          return t('SQF.Not Suitable')
        default:
          return t('Profile.Trading Experience Test Failed!')
      }
    }

    const getMessage = () => {
      switch (resolvedProductId) {
        case TickmillProductType.SQF:
          return [t('SQF.result.failed')]
        default:
          return [
            t('Profile.Maximum attempts (3/3) reached'),
            t('Profile.You will be able to re-take the appropriateness test in 1 year'),
          ]
      }
    }

    const content = (
      <ContentBlock
        variant='voucher'
        icon={
          <FeatureIcon
            size='lg'
            icon={CircleCloseIcon}
            bg='surface-danger-high-em'
            color='text-white'
          />
        }
        title={getTitle()}
        description={getMessage()}
      />
    )

    if (!isSQFProduct) {
      return <TestTemplate maxWidth={600}>{content}</TestTemplate>
    }

    return (
      <TestTemplate maxWidth={600} contentRef={contentRef}>
        {content}
        <FullScreenFooter contentRef={contentRef}>
          <Button.Primary fullWidth size='lg' onClick={handleClose}>
            {t('Close')}
          </Button.Primary>
        </FullScreenFooter>
      </TestTemplate>
    )
  }

  const description: (string | React.JSX.Element)[] = []

  if (isTimeLeft) {
    description.push(
      t(
        'Profile.Client Area and opening a Trading Account will only be available upon satisfactory completion of appropriateness test.'
      )
    )
    description.push(
      t(
        'Profile.Your next appropriateness test (attempts {{attempt}} of 3) is due in {{days}} days {{hours}} hours {{minutes}} minutes {{seconds}} seconds.',
        {
          attempt,
          days,
          hours,
          minutes,
          seconds,
        }
      )
    )
  } else {
    description.push(
      t(
        'Profile.You may fully use Client Area functions and open a live trading account after satisfying appropriateness test requirements. Please note that live trading account opening and Client Area functions will be frozen until you satisfy appropriateness test requirements.'
      )
    )
  }

  return (
    <TestTemplate maxWidth={600} isLoading={isLoading} contentRef={contentRef}>
      <ContentBlock
        variant='voucher'
        icon={<FeatureIcon size='lg' icon={CircleCloseIcon} bg='surface-danger-high-em' />}
        title={t('Profile.Trading Experience Test Failed!')}
        description={description}
      />
      <FullScreenFooter contentRef={contentRef}>
        <Button
          size='lg'
          fullWidth
          iconRight={isTimeLeft ? ArrowIcon.Right : undefined}
          onClick={handleClose}
        >
          {t('Close')}
        </Button>
      </FullScreenFooter>
    </TestTemplate>
  )
}
